/**
 * AgentsView Component
 *
 * Unified view for managing agents - replaces both AgentManager (admin) and UserAgentsView.
 * Uses adminMode prop to determine API behavior and available features.
 *
 * @module components/agents/AgentsView
 */

import { useState, useCallback, useEffect } from 'react';
import { Users, Bot, Share2, X, Loader2, Trash2, UserPlus, Shield } from 'lucide-react';
import AgentList, { AgentRow } from '../admin/AgentList.jsx';
import AgentForm from '../admin/AgentForm.jsx';
import ProvisioningModal from '../admin/ProvisioningModal.jsx';
import ConfirmDialog from '../common/ConfirmDialog.jsx';
import { Avatar } from '../common/Avatar.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';

// ============================================
// API Functions
// ============================================

/**
 * Fetch agents from the API
 * @param {boolean} includeAll - If true and user is admin, returns all agents
 * @returns {Promise<Array>} List of agents
 */
async function fetchAgents(includeAll = false) {
  const params = new URLSearchParams({ includeDisabled: 'true' });
  if (includeAll) {
    params.append('includeAll', 'true');
  }
  const response = await fetch(`/api/agents?${params}`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch agents: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch agents shared with the current user
 * @returns {Promise<Array>} List of shared agents
 */
async function fetchSharedAgents() {
  const response = await fetch('/api/agents/shared-with-me', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch shared agents: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch shares for a specific agent
 * @param {string} agentId - Agent ID
 * @returns {Promise<Array>} List of shares
 */
async function fetchAgentShares(agentId) {
  const response = await fetch(`/api/agents/${agentId}/shares`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch shares: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Share an agent with a user
 * @param {string} agentId - Agent ID
 * @param {string} userId - User ID to share with
 * @param {string} [note] - Optional note
 * @returns {Promise<object>} Created share
 */
async function shareAgent(agentId, userId, note) {
  const response = await fetch(`/api/agents/${agentId}/shares`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userId, note })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to share agent: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Fetch share counts for all agents
 * @param {Array<string>} agentIds - Array of agent IDs
 * @returns {Promise<Object>} Map of agent ID to share count
 */
async function fetchAgentShareCounts(agentIds) {
  const counts = {};
  // Fetch shares for each agent in parallel
  await Promise.all(
    agentIds.map(async (agentId) => {
      try {
        const shares = await fetchAgentShares(agentId);
        counts[agentId] = shares.length;
      } catch {
        counts[agentId] = 0;
      }
    })
  );
  return counts;
}

/**
 * Remove a share
 * @param {string} agentId - Agent ID
 * @param {string} shareId - Share ID
 * @returns {Promise<void>}
 */
async function removeShare(agentId, shareId) {
  const response = await fetch(`/api/agents/${agentId}/shares/${shareId}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to remove share: ${response.statusText}`);
  }
}

/**
 * Fetch all users for sharing
 * @returns {Promise<Array>} List of users
 */
async function fetchUsers() {
  const response = await fetch('/api/users', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Fetch hosts accessible to the current user
 * For admins: returns all hosts
 * For users: returns only hosts with UserHostAccess
 * @returns {Promise<Array>} List of accessible hosts
 */
async function fetchHosts() {
  const response = await fetch('/api/hosts/my-access', {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Failed to fetch hosts: ${response.statusText}`);
  }
  const data = await response.json();
  return data.data || [];
}

/**
 * Create a new agent slot
 * @param {object} agentData - Agent data
 * @returns {Promise<object>} Created agent
 */
async function createAgentSlot(agentData) {
  const response = await fetch('/api/agents', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(agentData)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to create agent: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Activate an agent slot (async - returns jobId)
 * @param {string} id - Agent ID
 * @returns {Promise<object>} { jobId, status, message }
 */
async function activateAgent(id) {
  const response = await fetch(`/api/agents/${id}/activate`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to activate agent: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Retry a failed provisioning job
 * @param {string} id - Agent ID
 * @returns {Promise<object>} { jobId, status, message }
 */
async function retryProvisioning(id) {
  const response = await fetch(`/api/agents/${id}/provisioning-retry`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to retry provisioning: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Enable an agent
 * @param {string} id - Agent ID
 * @returns {Promise<object>} Enabled agent
 */
async function enableAgent(id) {
  const response = await fetch(`/api/agents/${id}/enable`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to enable agent: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Disable an agent
 * @param {string} id - Agent ID
 * @returns {Promise<object>} Disabled agent
 */
async function disableAgent(id) {
  const response = await fetch(`/api/agents/${id}/disable`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to disable agent: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Terminate an agent
 * @param {string} id - Agent ID
 * @returns {Promise<void>}
 */
async function terminateAgent(id) {
  const response = await fetch(`/api/agents/${id}/terminate`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to terminate agent: ${response.statusText}`);
  }
}

/**
 * Delete an agent slot (only non-activated)
 * @param {string} id - Agent ID
 * @returns {Promise<void>}
 */
async function deleteAgentSlot(id) {
  const response = await fetch(`/api/agents/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to delete agent: ${response.statusText}`);
  }
}

/**
 * Start a session for an agent
 * @param {string} id - Agent ID
 * @param {string} [projectPath] - Optional project path
 * @returns {Promise<object>} Session data
 */
async function startAgentSession(id, projectPath) {
  const response = await fetch(`/api/agents/${id}/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ projectPath })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to start session: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Stop/terminate a session for an agent
 * @param {string} id - Agent ID
 * @returns {Promise<void>}
 */
async function stopAgentSession(id) {
  const response = await fetch(`/api/agents/${id}/session/terminate`, {
    method: 'POST',
    credentials: 'include',
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to stop session: ${response.statusText}`);
  }
}

/**
 * Update an agent
 * @param {string} id - Agent ID
 * @param {object} data - Update data
 * @returns {Promise<object>} Updated agent
 */
async function updateAgent(id, data) {
  const response = await fetch(`/api/agents/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to update agent: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Change an agent's chat mode
 * @param {string} id - Agent ID
 * @param {string} mode - New chat mode ('normal', 'muted', 'dnd')
 * @returns {Promise<object>} Updated agent
 */
async function changeAgentChatMode(id, mode) {
  const response = await fetch(`/api/agents/${id}/chat-mode`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ mode: mode.toLowerCase() })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to change chat mode: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Update agent grants via existing PUT endpoint
 * @param {string} id - Agent ID
 * @param {string[]} grants - Full grants array to set
 * @returns {Promise<object>} Updated agent data
 */
async function updateAgentGrants(id, grants) {
  const response = await fetch(`/api/agents/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ grants }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || `Failed to update grants: ${response.statusText}`);
  }
  return response.json();
}

// ============================================
// Share Dialog Component
// ============================================

/**
 * ShareDialog Component
 * Modal for managing agent shares - add/remove users
 */
function ShareDialog({ agent, onClose, onSharesChanged }) {
  const [shares, setShares] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removingShareId, setRemovingShareId] = useState(null);

  // Load shares and users
  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const [sharesData, usersData] = await Promise.all([
          fetchAgentShares(agent.id),
          fetchUsers()
        ]);
        setShares(sharesData);
        // Filter out users who already have access
        const sharedUserIds = new Set(sharesData.map(s => s.userId));
        setUsers(usersData.filter(u => !sharedUserIds.has(u.id) && u.id !== agent.ownerId));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [agent.id, agent.ownerId]);

  // Handle add share
  const handleAddShare = async () => {
    if (!selectedUserId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await shareAgent(agent.id, selectedUserId, note || undefined);
      // Reload shares
      const sharesData = await fetchAgentShares(agent.id);
      setShares(sharesData);
      // Remove user from available list
      setUsers(prev => prev.filter(u => u.id !== selectedUserId));
      setSelectedUserId('');
      setNote('');
      onSharesChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle remove share - show confirmation
  const handleRemoveShare = (shareId) => {
    setRemovingShareId(shareId);
  };

  // Confirm remove share
  const confirmRemoveShare = async () => {
    if (!removingShareId) return;
    setError(null);
    try {
      await removeShare(agent.id, removingShareId);
      // Find the removed user and add back to available list
      const removedShare = shares.find(s => s.id === removingShareId);
      if (removedShare?.user) {
        setUsers(prev => [...prev, removedShare.user]);
      }
      setShares(prev => prev.filter(s => s.id !== removingShareId));
      onSharesChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingShareId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-lg w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-purple-400" />
            <div>
              <h3 className="text-lg font-semibold text-white">Share Agent</h3>
              <p className="text-sm text-gray-400">{agent.displayName || agent.name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="p-3 rounded-md bg-red-500/20 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Add new share */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">Share with user</label>
                <div className="flex gap-2">
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="flex-1 px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select a user...</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName || user.username}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleAddShare}
                    disabled={!selectedUserId || isSubmitting}
                    className="px-4 py-2 rounded-md bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                  >
                    {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                    Add
                  </button>
                </div>
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Optional note..."
                  className="w-full px-3 py-2 rounded-md bg-gray-900 border border-gray-700 text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              {/* Current shares */}
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-300">
                  Shared with ({shares.length})
                </label>
                {shares.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Not shared with anyone yet</p>
                ) : (
                  <div className="space-y-2">
                    {shares.map((share) => (
                      <div
                        key={share.id}
                        className="flex items-center justify-between p-3 rounded-md bg-gray-900 border border-gray-700"
                      >
                        <div className="flex items-center gap-3">
                          <Avatar
                            src={share.user?.avatarUrl}
                            name={share.user?.displayName || share.user?.username}
                            size="sm"
                          />
                          <div>
                            <p className="text-sm font-medium text-white">
                              {share.user?.displayName || share.user?.username}
                            </p>
                            {share.note && (
                              <p className="text-xs text-gray-500">{share.note}</p>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveShare(share.id)}
                          className="p-2 rounded-md text-gray-400 hover:text-red-400 hover:bg-gray-800 transition-colors"
                          title="Remove share"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="w-full px-4 py-2 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-sm font-medium transition-colors"
          >
            Done
          </button>
        </div>
      </div>

      {/* Remove Share Confirmation */}
      <ConfirmDialog
        isOpen={!!removingShareId}
        title="Remove Share"
        itemName={shares.find(s => s.id === removingShareId)?.user?.displayName || 'this user'}
        message="Remove this share? The user will no longer have access to this agent."
        confirmLabel="Remove"
        variant="danger"
        onConfirm={confirmRemoveShare}
        onCancel={() => setRemovingShareId(null)}
      />
    </div>
  );
}

// ============================================
// Main Component
// ============================================

/**
 * AgentsView Component
 *
 * Unified view for agents with adminMode prop.
 * - adminMode=true: Shows all agents (for admin panel)
 * - adminMode=false: Shows only owned + shared agents (for user view)
 *
 * @param {Object} props
 * @param {boolean} [props.adminMode=false] - Whether to show all agents (admin) or only owned (user)
 */
export default function AgentsView({ adminMode = false }) {
  // Get current user for ownership checks
  const { user } = useAuth();

  // State
  const [agents, setAgents] = useState([]);
  const [sharedAgents, setSharedAgents] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [agentShareCounts, setAgentShareCounts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [sharingAgent, setSharingAgent] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  // Provisioning modal state
  const [provisioningAgent, setProvisioningAgent] = useState(null);
  const [provisioningJobId, setProvisioningJobId] = useState(null);

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    itemName: '',
    message: '',
    warning: '',
    confirmLabel: 'Confirm',
    variant: 'danger',
    onConfirm: null
  });

  const closeConfirmDialog = useCallback(() => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false, onConfirm: null }));
  }, []);

  const openConfirmDialog = useCallback((options) => {
    setConfirmDialog({
      isOpen: true,
      title: options.title || 'Confirm',
      itemName: options.itemName || '',
      message: options.message || '',
      warning: options.warning || '',
      confirmLabel: options.confirmLabel || 'Confirm',
      variant: options.variant || 'danger',
      onConfirm: options.onConfirm
    });
  }, []);

  // Load agents, shared agents, hosts, and share counts
  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Hosts are needed for agent creation (both admin and user views)
      // /api/hosts/my-access returns all hosts for admins, accessible hosts for users
      const [agentsData, sharedData, hostsData] = await Promise.all([
        fetchAgents(adminMode), // Pass adminMode to determine includeAll
        fetchSharedAgents(),
        fetchHosts() // Always fetch - endpoint filters based on user role
      ]);
      setAgents(agentsData);
      setSharedAgents(sharedData);
      setHosts(hostsData);

      // Load share counts for active agents (in background)
      const activeAgentIds = agentsData
        .filter(a => a.status === 'active')
        .map(a => a.id);
      if (activeAgentIds.length > 0) {
        fetchAgentShareCounts(activeAgentIds).then(setAgentShareCounts);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [adminMode]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleCreate = useCallback(async (formData) => {
    try {
      await createAgentSlot(formData);
      await loadData();
      setShowForm(false);
    } catch (err) {
      throw err; // Let form handle error display
    }
  }, [loadData]);

  const handleActivate = useCallback(async (agent) => {
    setActionLoadingId(agent.id);
    try {
      const result = await activateAgent(agent.id);
      // Open provisioning modal with jobId
      setProvisioningAgent(agent);
      setProvisioningJobId(result.jobId);
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoadingId(null);
    }
  }, []);

  const handleProvisioningRetry = useCallback(async (agent) => {
    await retryProvisioning(agent.id);
  }, []);

  const handleProvisioningComplete = useCallback(() => {
    loadData();
  }, [loadData]);

  const handleProvisioningClose = useCallback(() => {
    setProvisioningAgent(null);
    setProvisioningJobId(null);
    loadData();
  }, [loadData]);

  const handleEnable = useCallback(async (agent) => {
    setActionLoadingId(agent.id);
    try {
      await enableAgent(agent.id);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoadingId(null);
    }
  }, [loadData]);

  const handleDisable = useCallback((agent) => {
    openConfirmDialog({
      title: 'Disable Agent',
      itemName: agent.displayName || agent.name,
      message: `Disable agent "${agent.displayName || agent.name}"? The agent will be paused but can be re-enabled later.`,
      confirmLabel: 'Disable',
      variant: 'warning',
      onConfirm: async () => {
        closeConfirmDialog();
        setActionLoadingId(agent.id);
        try {
          await disableAgent(agent.id);
          await loadData();
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoadingId(null);
        }
      }
    });
  }, [loadData, openConfirmDialog, closeConfirmDialog]);

  const handleTerminate = useCallback((agent) => {
    openConfirmDialog({
      title: 'Terminate Agent',
      itemName: agent.displayName || agent.name,
      message: `Terminate agent "${agent.displayName || agent.name}"?`,
      warning: 'This action cannot be undone. The agent will be permanently deactivated.',
      confirmLabel: 'Terminate',
      variant: 'danger',
      onConfirm: async () => {
        closeConfirmDialog();
        setActionLoadingId(agent.id);
        try {
          await terminateAgent(agent.id);
          await loadData();
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoadingId(null);
        }
      }
    });
  }, [loadData, openConfirmDialog, closeConfirmDialog]);

  const handleDelete = useCallback((agent) => {
    openConfirmDialog({
      title: 'Delete Agent Slot',
      itemName: agent.displayName || agent.name,
      message: `Delete agent slot "${agent.displayName || agent.name}"?`,
      warning: 'This can only be done for non-activated agents.',
      confirmLabel: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        closeConfirmDialog();
        setActionLoadingId(agent.id);
        try {
          await deleteAgentSlot(agent.id);
          await loadData();
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoadingId(null);
        }
      }
    });
  }, [loadData, openConfirmDialog, closeConfirmDialog]);

  const handleSettings = useCallback((agent) => {
    setEditingAgent(agent);
  }, []);

  const handleShare = useCallback((agent) => {
    setSharingAgent(agent);
  }, []);

  const handleUpdate = useCallback(async (formData) => {
    if (!editingAgent) return;
    try {
      await updateAgent(editingAgent.id, formData);
      await loadData();
      setEditingAgent(null);
    } catch (err) {
      throw err; // Let form handle error display
    }
  }, [editingAgent, loadData]);

  const handleStartSession = useCallback(async (agent) => {
    setActionLoadingId(agent.id);
    try {
      await startAgentSession(agent.id, agent.workingDirectory);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoadingId(null);
    }
  }, [loadData]);

  const handleStopSession = useCallback((agent) => {
    openConfirmDialog({
      title: 'Stop Session',
      itemName: agent.displayName || agent.name,
      message: `Stop session for "${agent.displayName || agent.name}"?`,
      confirmLabel: 'Stop',
      variant: 'warning',
      onConfirm: async () => {
        closeConfirmDialog();
        setActionLoadingId(agent.id);
        try {
          await stopAgentSession(agent.id);
          await loadData();
        } catch (err) {
          alert(err.message);
        } finally {
          setActionLoadingId(null);
        }
      }
    });
  }, [loadData, openConfirmDialog, closeConfirmDialog]);

  const handleChatModeChange = useCallback(async (agent, newMode) => {
    setActionLoadingId(agent.id);
    try {
      await changeAgentChatMode(agent.id, newMode);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoadingId(null);
    }
  }, [loadData]);

  const handleToggleEditGrant = useCallback(async (agent) => {
    const currentGrants = agent.grants || [];
    const hasGrant = currentGrants.includes('claude-md-edit');
    const newGrants = hasGrant
      ? currentGrants.filter(g => g !== 'claude-md-edit')
      : [...currentGrants, 'claude-md-edit'];
    setActionLoadingId(agent.id);
    try {
      await updateAgentGrants(agent.id, newGrants);
      await loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setActionLoadingId(null);
    }
  }, [loadData]);

  // Render
  return (
    <div className="p-6">
      {/* Create Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <AgentForm
              hosts={hosts}
              onSubmit={handleCreate}
              onCancel={() => setShowForm(false)}
            />
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {editingAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <AgentForm
              hosts={hosts}
              agent={editingAgent}
              onSubmit={handleUpdate}
              onCancel={() => setEditingAgent(null)}
            />
          </div>
        </div>
      )}

      {/* Share Dialog Modal */}
      {sharingAgent && (
        <ShareDialog
          agent={sharingAgent}
          onClose={() => setSharingAgent(null)}
          onSharesChanged={loadData}
        />
      )}

      {/* Provisioning Modal */}
      <ProvisioningModal
        isOpen={!!provisioningAgent}
        agent={provisioningAgent}
        jobId={provisioningJobId}
        onClose={handleProvisioningClose}
        onRetry={handleProvisioningRetry}
        onComplete={handleProvisioningComplete}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        itemName={confirmDialog.itemName}
        message={confirmDialog.message}
        warning={confirmDialog.warning}
        confirmLabel={confirmDialog.confirmLabel}
        variant={confirmDialog.variant}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirmDialog}
      />

      {/* Shared with me Section */}
      {sharedAgents.length > 0 && (
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-purple-500/10">
              <Share2 className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Shared with me</h2>
              <p className="text-sm text-gray-400">
                {sharedAgents.length} agent{sharedAgents.length !== 1 ? 's' : ''} shared by others
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {sharedAgents.map((share) => (
              <div key={share.id} className="relative">
                <AgentRow
                  agent={share.agent}
                  onStartSession={handleStartSession}
                  onStopSession={handleStopSession}
                  isActionLoading={actionLoadingId === share.agent?.id}
                />
                {/* Shared by indicator */}
                <div className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full bg-purple-500/20 border border-purple-500/30">
                  <Users className="w-3 h-3 text-purple-400" />
                  <span className="text-xs text-purple-300">
                    Shared by {share.agent?.owner?.displayName || share.agent?.owner?.username || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Agents List */}
      <AgentList
        agents={agents}
        loading={isLoading}
        error={error}
        onRefresh={loadData}
        onCreate={() => setShowForm(true)}
        onActivate={handleActivate}
        onEnable={handleEnable}
        onDisable={handleDisable}
        onTerminate={handleTerminate}
        onDelete={handleDelete}
        onSettings={handleSettings}
        onShare={handleShare}
        onToggleEditGrant={handleToggleEditGrant}
        onChatModeChange={handleChatModeChange}
        agentShareCounts={agentShareCounts}
        onStartSession={handleStartSession}
        onStopSession={handleStopSession}
        actionLoadingId={actionLoadingId}
        currentUserId={user?.id}
      />
    </div>
  );
}
